import React, { useEffect, useMemo, useRef, useState } from 'react';
import Board from '@cloudscape-design/board-components/board';
import BoardItem from '@cloudscape-design/board-components/board-item';
import {
  Alert, Box, Button, ButtonDropdown, CollectionPreferences, DatePicker, FormField, Header, Link,
  Modal, Pagination, Popover, Select, SpaceBetween, StatusIndicator, Table, TextFilter,
} from '@cloudscape-design/components';
import { apiFetch } from '../../auth/apiClient';
import { buildNotificationTargetPath } from '../../utils/notificationTarget';
import { boardItemI18nStrings } from '../finance/widgets/common';
import useReminderView from './useReminderView';
import RemindersHelp from '../../helpPanelContents/remindersHelp';

// Presentation only: preserve the original title in Details and all record actions.
export function ReminderTitleCell({ item }) {
  const originalTitle = item.title || 'Reminder';
  const displayTitle = item.actionPlanId && item.category === 'Action plan'
    && /^Action plan review — .+/.test(originalTitle) ? 'Action plan review' : originalTitle;
  return <SpaceBetween size="xxs">
    <div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
      overflow: 'hidden', overflowWrap: 'anywhere', whiteSpace: 'normal' }}>{displayTitle}</div>
    <Popover header="Reminder details" triggerType="text-inline" triggerAriaLabel={`Details for ${originalTitle}`} content={<SpaceBetween size="s">
      <div style={{ fontWeight: 700, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{originalTitle}</div>
      <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {item.description?.trim() ? item.description : originalTitle}
      </div>
    </SpaceBetween>} dismissAriaLabel="Close reminder details" renderWithPortal>
      Details
    </Popover>
  </SpaceBetween>;
}

const LAYOUT_KEY = 'path.reminders.layout.v1';
const PREFERENCES_KEY = 'path.reminders.table.v1';
const WIDGET_ID = 'outstanding-reminders';
const definition = { defaultRowSpan: 7, defaultColumnSpan: 4, minRowSpan: 4, minColumnSpan: 2 };
const widgetData = { title: 'Outstanding reminders', description: 'Plan follow-ups and acknowledge completed reminder work.' };
const defaultLayout = [{ id: WIDGET_ID, rowSpan: 7, columnSpan: 4 }];
const reminderRecordPath = item => buildNotificationTargetPath({
  caseId: item.caseId, applicationId: item.applicationId, isCaseManaged: !item.applicationId,
});
const columns = [
  ['clientName', 'Client'], ['title', 'Reminder'], ['timing', 'Timing'], ['caseManagerName', 'Case Manager'],
  ['actions', 'Actions'], ['dueAt', 'Due date'], ['assignedStaffName', 'Assigned to'], ['category', 'Category'], ['caseNumber', 'Case number'],
];
const defaultVisible = new Set(['clientName', 'title', 'timing', 'caseManagerName', 'actions']);
const sortFields = new Set(['dueAt', 'title', 'clientName', 'caseManagerName', 'assignedStaffName', 'category']);
const timingOptions = [
  { value: 'all', label: 'All outstanding' }, { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' }, { value: 'upcoming', label: 'Upcoming' },
  { value: 'undated', label: 'No due date' },
];

function normalizePreferences(saved = {}) {
  const source = saved && typeof saved === 'object' ? saved : {};
  const known = new Set(columns.map(([id]) => id));
  const display = Array.isArray(source.contentDisplay) ? source.contentDisplay : [];
  const savedIds = [...new Set([...display.map(item => item?.id).filter(id => known.has(id)), ...known])];
  // Upgrade existing table preferences once, retaining widths, filters and page size.
  const migrateOrder = source.columnOrderVersion !== 3;
  const ids = migrateOrder ? [...known] : savedIds;
  return {
    columnOrderVersion: 3,
    pageSize: [10, 25, 50, 100].includes(source.pageSize) ? source.pageSize : 25,
    timing: timingOptions.some(option => option.value === source.timing) ? source.timing : 'all',
    sort: sortFields.has(source.sort) ? source.sort : 'dueAt',
    direction: source.direction === 'desc' ? 'desc' : 'asc',
    contentDisplay: ids.map(id => ({ id, visible: migrateOrder ? defaultVisible.has(id) : ['title', 'actions'].includes(id)
      || (display.find(item => item?.id === id)?.visible ?? defaultVisible.has(id)) })),
    widths: Object.fromEntries(Object.entries(source.widths || {}).filter(([id, width]) =>
      known.has(id) && Number.isFinite(width) && width >= 80)),
  };
}

function loadPreferences() {
  try { return normalizePreferences(JSON.parse(localStorage.getItem(PREFERENCES_KEY) || '{}')); }
  catch (_) { return normalizePreferences(); }
}

function normalizeLayout(items) {
  const item = Array.isArray(items) && items.find(candidate => candidate?.id === WIDGET_ID);
  return item ? [{ id: WIDGET_ID,
    rowSpan: Number.isInteger(item.rowSpan) && item.rowSpan >= 4 ? item.rowSpan : 7,
    columnSpan: Number.isInteger(item.columnSpan) && item.columnSpan >= 2 && item.columnSpan <= 4 ? item.columnSpan : 4,
    ...(Number.isInteger(item.columnOffset) && item.columnOffset >= 0 && item.columnOffset <= 2
      ? { columnOffset: item.columnOffset } : {}),
  }] : [];
}

function loadLayout() {
  try {
    const saved = JSON.parse(localStorage.getItem(LAYOUT_KEY));
    if (Array.isArray(saved)) return normalizeLayout(saved);
  } catch (_) { /* Use the default when saved preferences are unavailable. */ }
  return defaultLayout;
}

const boardI18nStrings = {
  liveAnnouncementDndStarted: operation => operation === 'resize' ? 'Resizing' : 'Dragging',
  liveAnnouncementDndItemReordered: operation => `Item moved to ${operation.direction === 'horizontal'
    ? `column ${operation.placement.x + 1}` : `row ${operation.placement.y + 1}`}.`,
  liveAnnouncementDndItemResized: operation => {
    const horizontal = operation.direction === 'horizontal';
    return `Item resized to ${horizontal ? `columns ${operation.placement.width}` : `rows ${operation.placement.height}`}${
      (horizontal ? operation.isMinimalColumnsReached : operation.isMinimalRowsReached) ? ' (minimal)' : ''}.`;
  },
  liveAnnouncementDndItemInserted: operation => `Item inserted to column ${operation.placement.x + 1}, row ${operation.placement.y + 1}.`,
  liveAnnouncementDndCommitted: operation => `${operation} committed`,
  liveAnnouncementDndDiscarded: operation => `${operation} discarded`,
  liveAnnouncementItemRemoved: operation => `Removed item ${operation.item.data.title}.`,
  navigationAriaLabel: 'Reminders dashboard navigation',
  navigationAriaDescription: 'Use arrow keys to move between widgets on the Reminders dashboard.',
  navigationItemAriaLabel: item => item ? item.data.title : 'Empty',
};

const dateFormatter = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
const formatDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '')
  ? dateFormatter.format(new Date(`${value}T12:00:00Z`)) : '—';
const categoryLabel = value => value ? String(value).replace(/[_-]+/g, ' ').replace(/^./, letter => letter.toUpperCase()) : '—';
const timingLabel = (item, businessDate) => {
  if (['overdue', 'upcoming'].includes(item.timing)
    && /^\d{4}-\d{2}-\d{2}$/.test(item.dueDate || '')
    && /^\d{4}-\d{2}-\d{2}$/.test(businessDate || '')) {
    // Subtract date keys, not local timestamps: a daylight-saving day still counts as one day.
    const days = Math.abs(Math.round((Date.parse(`${item.dueDate}T12:00:00Z`)
      - Date.parse(`${businessDate}T12:00:00Z`)) / 86400000));
    if (Number.isFinite(days)) return item.timing === 'overdue'
      ? `${days} day${days === 1 ? '' : 's'} overdue` : `Due in ${days} day${days === 1 ? '' : 's'}`;
  }
  return timingOptions.find(option => option.value === item.timing)?.label || 'No due date';
};
const requestError = status => status === 403 ? 'You no longer have access to these reminders.'
  : 'Unable to load reminders. Please try again.';

export function buildReminderAcknowledgementRequest(item) {
  const expectedCaseId = Number(item?.caseId);
  const expectedApplicationId = item?.applicationId == null ? null : Number(item.applicationId);
  if (!Number.isSafeInteger(expectedCaseId) || expectedCaseId <= 0
    || (expectedApplicationId !== null && (!Number.isSafeInteger(expectedApplicationId) || expectedApplicationId <= 0))) return null;
  return { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(expectedApplicationId === null
      ? { scopeMode: 'case', expectedCaseId }
      : { scopeMode: 'application', expectedCaseId, expectedApplicationId }),
  };
}

export function buildReminderRescheduleRequest(item, date) {
  const scope = buildReminderAcknowledgementRequest(item);
  if (!scope || !/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return null;
  const dueAt = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(dueAt.getTime()) || dueAt.toISOString().slice(0, 10) !== date) return null;
  return { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
    ...JSON.parse(scope.body), expectedDueAt: item.dueAt || null, dueAt: dueAt.toISOString(),
  }) };
}

export function RemindersTableWidget({ actions, toggleHelpPanel }) {
  const { view, setView, options: viewOptions, identityKey, ready: viewReady } = useReminderView();
  const [preferences, setPreferences] = useState(loadPreferences);
  const [text, setText] = useState('');
  const [queryText, setQueryText] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledgementError, setAcknowledgementError] = useState('');
  const [success, setSuccess] = useState('');
  const [rescheduling, setRescheduling] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleError, setRescheduleError] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const busy = acknowledging || savingDate;
  const preferencesSignature = useRef(null);
  const mounted = useRef(false);
  const mutationPending = useRef(false);
  const { timing, sort, direction, pageSize } = preferences;

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const signature = JSON.stringify(preferences);
    if (signature === preferencesSignature.current) return;
    preferencesSignature.current = signature;
    try { localStorage.setItem(PREFERENCES_KEY, signature); } catch (_) { /* Keep the current session usable. */ }
  }, [preferences]);

  useEffect(() => {
    if (!view || !viewReady) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true); setError(''); setData(null);
    const params = new URLSearchParams({ view, text: queryText, timing, sort, direction, pageIndex: String(pageIndex), pageSize: String(pageSize) });
    (async () => {
      try {
        const response = await apiFetch(`/api/me/reminders?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error(requestError(response.status));
        const result = await response.json();
        if (!Array.isArray(result.items) || !Number.isFinite(result.total) || result.total < 0) throw new Error('Unable to load reminders. Please try again.');
        if (cancelled) return;
        const lastPage = Math.max(1, Math.ceil(result.total / pageSize));
        if (pageIndex > lastPage) { setPageIndex(lastPage); return; }
        setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load reminders. Please try again.');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [view, viewReady, identityKey, queryText, timing, sort, direction, pageIndex, pageSize, refresh]);

  const acknowledge = async () => {
    if (!confirmation || mutationPending.current) return;
    const request = buildReminderAcknowledgementRequest(confirmation);
    if (!request) { setAcknowledgementError('The reminder no longer has a valid record link. Cancel and refresh the list.'); return; }
    mutationPending.current = true;
    setAcknowledging(true); setAcknowledgementError('');
    try {
      const response = await apiFetch(`/api/me/reminders/${confirmation.id}/acknowledge`, request);
      if (!response.ok) throw new Error(response.status === 403 ? 'You no longer have permission to acknowledge this reminder.'
        : [404, 409].includes(response.status) ? 'This reminder has changed. Cancel and refresh the list before trying again.'
          : 'Unable to acknowledge the reminder. Please try again.');
      const result = await response.json();
      if (result.ok !== true) throw new Error('Unable to confirm acknowledgement. Refresh the list before trying again.');
      window.dispatchEvent(new CustomEvent('case-reminders-refresh'));
      if (!mounted.current) return;
      setSuccess(`Acknowledged “${confirmation.title || 'Reminder'}”.`);
      setConfirmation(null); setRefresh(value => value + 1);
    } catch (err) { if (mounted.current) setAcknowledgementError(err.message || 'Unable to acknowledge the reminder. Please try again.'); }
    finally { mutationPending.current = false; if (mounted.current) setAcknowledging(false); }
  };

  const reschedule = async () => {
    if (!rescheduling || mutationPending.current) return;
    const request = buildReminderRescheduleRequest(rescheduling, rescheduleDate);
    if (!request) { setRescheduleError('Choose a valid date in YYYY-MM-DD format.'); return; }
    mutationPending.current = true; setSavingDate(true); setRescheduleError('');
    try {
      const response = await apiFetch(`/api/reminders/${rescheduling.id}`, request);
      if (!response.ok) throw new Error(response.status === 403 ? 'You no longer have permission to reschedule this reminder.'
        : [404, 409].includes(response.status) ? 'This reminder has changed. Cancel and refresh the list before trying again.'
          : 'Unable to reschedule the reminder. Please try again.');
      const result = await response.json();
      if (Number(result.id) !== Number(rescheduling.id)) throw new Error('Unable to confirm the new date. Refresh the list before trying again.');
      window.dispatchEvent(new CustomEvent('case-reminders-refresh'));
      window.dispatchEvent(new CustomEvent('case-notes-refresh', { detail: { caseId: rescheduling.caseId } }));
      if (!mounted.current) return;
      setSuccess(`Rescheduled “${rescheduling.title || 'Reminder'}” to ${formatDate(rescheduleDate)}.`);
      setRescheduling(null); setRefresh(value => value + 1);
    } catch (err) { if (mounted.current) setRescheduleError(err.message); }
    finally { mutationPending.current = false; if (mounted.current) setSavingDate(false); }
  };

  const visibleColumns = preferences.contentDisplay.filter(item => item.visible);
  const definitions = visibleColumns.map(({ id }) => ({
    id, header: columns.find(column => column[0] === id)[1],
    ...(id === 'timing' ? { sortingField: 'dueAt' } : sortFields.has(id) ? { sortingField: id } : {}),
    width: preferences.widths[id] || ({ title: 230, clientName: 160, dueAt: 145, timing: 160, caseManagerName: 180, actions: 290 }[id] || 170),
    minWidth: id === 'title' ? 200 : id === 'actions' ? 285 : 120,
    cell: item => {
      if (id === 'title') return <ReminderTitleCell item={item} />;
      if (id === 'clientName') {
        const href = reminderRecordPath(item);
        return href && item.clientName ? <Link href={href}>{item.clientName}</Link> : item.clientName || '—';
      }
      if (id === 'dueAt') return formatDate(item.dueDate);
      if (id === 'timing') return <StatusIndicator type={item.timing === 'overdue' ? 'error' : item.timing === 'today' ? 'warning' : 'info'}>
        {timingLabel(item, data?.businessDate)}</StatusIndicator>;
      if (id === 'category') return categoryLabel(item.category);
      if (id === 'actions') {
        return item.canAcknowledge ? <span style={{ whiteSpace: 'nowrap' }}>
          <Button variant="inline-link" disabled={loading || busy} onClick={() => {
            setAcknowledgementError(''); setConfirmation(item);
          }} ariaLabel={`Acknowledge reminder: ${item.title || 'Reminder'}`}>Acknowledge reminder</Button>{', '}
          <Button variant="inline-link" disabled={loading || busy} onClick={() => {
            setRescheduling(item); setRescheduleError('');
            setRescheduleDate(item.dueDate || '');
          }} ariaLabel={`Reschedule reminder: ${item.title || 'Reminder'}`}>Reschedule</Button>
        </span> : '—';
      }
      return item[id] || '—';
    },
  }));

  return <BoardItem i18nStrings={boardItemI18nStrings}
    header={<Header variant="h2" counter={data ? `(${data.total})` : undefined}
      info={<Link variant="info" onFollow={event => {
        event.preventDefault(); toggleHelpPanel?.(<RemindersHelp />, 'Reminders', RemindersHelp.aiContext);
      }}>Info</Link>}
      description={viewOptions.find(option => option.value === view)?.hint}
      actions={<Button iconName="refresh" disabled={loading || busy} onClick={() => {
        setSuccess(''); setRefresh(value => value + 1);
        window.dispatchEvent(new CustomEvent('case-reminders-refresh'));
      }}>Refresh</Button>}>{timing === 'all' ? 'Outstanding reminders' : timingOptions.find(option => option.value === timing)?.label}</Header>}
    settings={<ButtonDropdown ariaLabel="Reminders widget settings" variant="icon" disabled={busy}
      items={[{ id: 'remove', text: 'Remove widget' }]}
      onItemClick={({ detail }) => { if (detail.id === 'remove') actions.removeItem(); }} />}>
    <SpaceBetween size="m">
      {error && <Alert type="error" action={<Button onClick={() => setRefresh(value => value + 1)}>Retry</Button>}>{error}</Alert>}
      {success && <Alert type="success" dismissible onDismiss={() => setSuccess('')}>{success}</Alert>}
      <Table variant="embedded" items={data?.items || []} trackBy="id" columnDefinitions={definitions}
        loading={loading} loadingText="Loading reminders" resizableColumns stickyHeader enableKeyboardNavigation
        ariaLabels={{ tableLabel: 'Outstanding reminders', resizerRoleDescription: 'Column resize handle' }}
        sortingColumn={definitions.find(column => column.id === sort) || { sortingField: sort }}
        sortingDescending={direction === 'desc'}
        onSortingChange={({ detail }) => {
          if (busy || !sortFields.has(detail.sortingColumn.sortingField)) return;
          setPageIndex(1); setPreferences(previous => ({ ...previous,
            sort: detail.sortingColumn.sortingField, direction: detail.isDescending ? 'desc' : 'asc' }));
        }}
        onColumnWidthsChange={({ detail }) => setPreferences(previous => normalizePreferences({ ...previous,
          widths: { ...previous.widths, ...Object.fromEntries(detail.widths.map((width, index) => [visibleColumns[index]?.id, width])) },
        }))}
        filter={<SpaceBetween direction="horizontal" size="s">
          <TextFilter filteringText={text} filteringPlaceholder="Find reminder, client or staff"
            filteringAriaLabel="Find reminder, client or staff" disabled={busy} loading={loading}
            onChange={({ detail }) => setText(detail.filteringText)}
            onDelayedChange={({ detail }) => { setQueryText(detail.filteringText); setPageIndex(1); }} />
          <Select ariaLabel="Reminder timing" options={timingOptions} selectedOption={timingOptions.find(option => option.value === timing)}
            disabled={busy} onChange={({ detail }) => {
              setPageIndex(1); setPreferences(previous => ({ ...previous, timing: detail.selectedOption.value }));
            }} />
          {viewOptions.length > 1 && <Select ariaLabel="Reminder view" options={viewOptions}
            selectedOption={viewOptions.find(option => option.value === view)} disabled={busy || !viewReady}
            onChange={({ detail }) => { setPageIndex(1); setView(detail.selectedOption.value); }} />}
        </SpaceBetween>}
        pagination={<Pagination currentPageIndex={pageIndex} pagesCount={Math.max(1, Math.ceil((data?.total || 0) / pageSize))}
          disabled={loading || busy} ariaLabels={{ nextPageLabel: 'Next page', previousPageLabel: 'Previous page', pageLabel: page => `Page ${page}` }}
          onChange={({ detail }) => setPageIndex(detail.currentPageIndex)} />}
        preferences={<CollectionPreferences title="Table preferences" confirmLabel="Confirm" cancelLabel="Cancel"
          disabled={busy} preferences={preferences} onConfirm={({ detail }) => {
            setPageIndex(1); setPreferences(previous => normalizePreferences({ ...previous, ...detail }));
          }}
          pageSizePreference={{ title: 'Rows per page', options: [10, 25, 50, 100].map(value => ({ value, label: `${value} reminders` })) }}
          contentDisplayPreference={{ title: 'Columns', description: 'Choose the details to display and their order.',
            options: columns.map(([id, label]) => ({ id, label, alwaysVisible: ['title', 'actions'].includes(id) })) }} />}
        empty={<Box padding="m" textAlign="center">{error ? 'Reminders could not be loaded.'
          : text || timing !== 'all' ? 'No outstanding reminders match these filters.' : 'No outstanding reminders.'}</Box>} />
    </SpaceBetween>
    <Modal visible={!!confirmation} header="Acknowledge reminder" onDismiss={() => { if (!mutationPending.current) setConfirmation(null); }}
      closeAriaLabel="Close acknowledgement" footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" disabled={busy} onClick={() => setConfirmation(null)}>Cancel</Button>
        <Button variant="primary" loading={acknowledging} onClick={acknowledge}>Acknowledge reminder</Button>
      </SpaceBetween></Box>}>
      <SpaceBetween size="m">
        {acknowledgementError && <Alert type="error">{acknowledgementError}</Alert>}
        <Box>Acknowledge “{confirmation?.title || 'Reminder'}” and clear its linked note's follow-up date?</Box>
      </SpaceBetween>
    </Modal>
    <Modal visible={!!rescheduling} header="Reschedule reminder" closeAriaLabel="Close reschedule"
      onDismiss={() => { if (!mutationPending.current) setRescheduling(null); }}
      footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" disabled={savingDate} onClick={() => setRescheduling(null)}>Cancel</Button>
        <Button variant="primary" loading={savingDate}
          disabled={!buildReminderRescheduleRequest(rescheduling, rescheduleDate)
            || rescheduleDate === (rescheduling?.dueDate || '')} onClick={reschedule}>Reschedule</Button>
      </SpaceBetween></Box>}>
      <SpaceBetween size="m">
        <Box>{rescheduling?.title || 'Reminder'}</Box>
        <FormField label="New reminder date" errorText={rescheduleError}
          constraintText={`Current due date: ${formatDate(rescheduling?.dueDate)}`}>
          <DatePicker value={rescheduleDate} placeholder="YYYY-MM-DD" ariaLabel="New reminder date" disabled={savingDate}
            onChange={({ detail }) => { setRescheduleDate(detail.value); setRescheduleError(''); }} />
        </FormField>

      </SpaceBetween>
    </Modal>
  </BoardItem>;
}

export default function RemindersDashboard({ updateBreadcrumbs, setAvailableItems, setSplitPanelOpen, toggleHelpPanel }) {
  const [layout, setLayout] = useState(loadLayout);
  const paletteSignature = useRef(null);
  const layoutSignature = useRef(null);
  const items = useMemo(() => layout.map(item => ({ ...item, definition, data: widgetData })), [layout]);
  const palette = useMemo(() => layout.length ? [] : [{ id: WIDGET_ID, definition, data: widgetData }], [layout]);

  useEffect(() => {
    updateBreadcrumbs?.([{ text: 'Home', href: '/' }, { text: 'Reminders', href: '/reminders' }]);
  }, [updateBreadcrumbs]);
  useEffect(() => {
    const nextPalette = JSON.stringify(palette);
    if (nextPalette !== paletteSignature.current) {
      paletteSignature.current = nextPalette; setAvailableItems?.(palette);
    }
    const nextLayout = JSON.stringify(layout);
    if (nextLayout !== layoutSignature.current) {
      layoutSignature.current = nextLayout;
      try { localStorage.setItem(LAYOUT_KEY, nextLayout); } catch (_) { /* Keep the board usable without storage. */ }
    }
  }, [layout, palette, setAvailableItems]);
  useEffect(() => {
    const add = event => {
      if (event.detail?.id === WIDGET_ID) setLayout(previous => previous.length ? previous : defaultLayout);
    };
    const open = () => { setAvailableItems?.(palette); setSplitPanelOpen?.(true); };
    const reset = () => setLayout(previous => JSON.stringify(previous) === JSON.stringify(defaultLayout) ? previous : defaultLayout);
    window.addEventListener('palette:add', add);
    window.addEventListener('reminders:openPalette', open);
    window.addEventListener('reminders:resetLayout', reset);
    return () => {
      window.removeEventListener('palette:add', add);
      window.removeEventListener('reminders:openPalette', open);
      window.removeEventListener('reminders:resetLayout', reset);
    };
  }, [palette, setAvailableItems, setSplitPanelOpen]);

  return <Board items={items} i18nStrings={boardI18nStrings}
    onItemsChange={({ detail }) => {
      const next = normalizeLayout(detail.items);
      setLayout(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    }} renderItem={(item, actions) => <RemindersTableWidget actions={actions} toggleHelpPanel={toggleHelpPanel} />}
    empty={<Box padding="m"><SpaceBetween size="s">
      <Box>No widgets on the Reminders dashboard. Use Add widget to choose a widget.</Box>
      <Button onClick={() => window.dispatchEvent(new CustomEvent('reminders:resetLayout'))}>Restore defaults</Button>
    </SpaceBetween></Box>} />;
}
